/**
 * @file PPD Purchase reducer.
 */

import pick from 'lodash.pick';
import get from 'lodash.get';

import {
  SELECT_DOWNLOAD,
  DESELECT_DOWNLOAD,
  SELECT_INDIVIDUAL_PRINT,
  DESELECT_INDIVIDUAL_PRINT,
  CHECKOUT_REQUEST,
  CHECKOUT_SUCCESS,
  CHECKOUT_FAILURE,
  SELECT_PRINT_PACKAGE,
  DESELECT_PRINT_PACKAGE,
  SELECT_IDENTITY,
  SELECT_SUBSCRIPTION,
  DESELECT_SUBSCRIPTION,
  SELECT_ALBUM_CUSTOM_PRINT,
  DESELECT_ALBUM_CUSTOM_PRINT,
  DESELECT_CART_ITEM,
} from '../actions/ppd-purchase';
import { PPD_CART_ITEM_TYPES } from '../constants/ppd-purchase';
import uuidv4 from '../util/uuid';

const initialState = {
  cart: [],
  loading: false,
  error: '',
  success: false,
  selectedIdentity: null,
  purchasedItems: [],
};

const ppdPurchaseReducer = (state = initialState, action = {}) => {
  /**
   * Given an item, cartItem and optionally accessors, confirms if
   * cartItem & item are the same.
   *
   * @param {Object} item The item to look for.
   * @param {Object} cartItem The cartItem to compare to.
   * @param {string} [sizePackageAccessor] The printSize or printPackage key accessor.
   * @returns {boolean} Whether the item and cartItem are the same or not.
   */
  const isExistingItem = (item, cartItem, sizePackageAccessor) => {
    if (item.type === PPD_CART_ITEM_TYPES.ALBUM_CUSTOM_PRINT) {
      return (
        get(item, 'selectedIdentity.id') ===
          get(cartItem, 'selectedIdentity.id') &&
        get(item, 'albumCustomPrint.id') ===
          get(cartItem, 'albumCustomPrint.id')
      );
    }

    return (
      get(cartItem, 'photo.id') === get(item, 'photo.id') &&
      get(cartItem, 'type') === item.type &&
      // Covers the case of downloads.
      (!sizePackageAccessor ||
        // Covers individual prints or printPackages.
        get(cartItem, `${sizePackageAccessor}.id`) ===
          get(item, `${sizePackageAccessor}.id`))
    );
  };

  const newItem = () => ({
    id: uuidv4(),
    ...pick(action, [
      'photo',
      'printPackage',
      'printSize',
      'quantity',
      'digitalDownloadUpsell',
      'selectedIdentity',
    ]),
  });

  switch (action.type) {
    case SELECT_DOWNLOAD:
      return {
        ...state,
        cart: [
          ...state.cart,
          {
            ...newItem(),
            type: PPD_CART_ITEM_TYPES.DOWNLOAD,
            selectedIdentity: state.selectedIdentity,
          },
        ],
      };

    case DESELECT_DOWNLOAD:
      return {
        ...state,
        cart: state.cart.filter(cartItem => {
          return !isExistingItem(
            { ...newItem(), type: PPD_CART_ITEM_TYPES.DOWNLOAD },
            cartItem,
          );
        }),
      };

    case SELECT_INDIVIDUAL_PRINT: {
      const isInCart = state.cart.find(cartItem =>
        isExistingItem(
          {
            ...newItem(),
            type: PPD_CART_ITEM_TYPES.INDIVIDUAL_PRINT,
          },
          cartItem,
          'printSize',
        ),
      );

      if (isInCart) {
        return {
          ...state,
          cart: state.cart.map(
            // since it exists, update:
            cartItem => {
              if (
                isExistingItem(
                  { ...newItem(), type: PPD_CART_ITEM_TYPES.INDIVIDUAL_PRINT },
                  cartItem,
                  'printSize',
                )
              ) {
                return { ...cartItem, quantity: action.quantity };
              }

              return cartItem;
            },
          ),
        };
      }

      return {
        ...state,
        cart: [
          ...state.cart,
          {
            ...newItem(),
            selectedIdentity: state.selectedIdentity,
            type: PPD_CART_ITEM_TYPES.INDIVIDUAL_PRINT,
          },
        ],
      };
    }

    case DESELECT_INDIVIDUAL_PRINT:
      return {
        ...state,
        cart: state.cart.filter(cartItem => {
          return !isExistingItem(
            { ...newItem(), type: PPD_CART_ITEM_TYPES.INDIVIDUAL_PRINT },
            cartItem,
            'printSize',
          );
        }),
      };

    case SELECT_PRINT_PACKAGE: {
      const isInCart = state.cart.find(cartItem =>
        isExistingItem(
          {
            ...newItem(),
            type: PPD_CART_ITEM_TYPES.PRINT_PACKAGE,
          },
          cartItem,
          'printPackage',
        ),
      );

      if (isInCart) {
        return {
          ...state,
          cart: state.cart.map(
            // since it exists, update:
            cartItem => {
              if (
                isExistingItem(
                  { ...newItem(), type: PPD_CART_ITEM_TYPES.PRINT_PACKAGE },
                  cartItem,
                  'printPackage',
                )
              ) {
                return {
                  ...cartItem,
                  quantity: action.quantity,
                  digitalDownloadUpsell: action.digitalDownloadUpsell,
                };
              }

              return cartItem;
            },
          ),
        };
      }

      return {
        ...state,
        cart: [
          ...state.cart,
          {
            ...newItem(),
            selectedIdentity: state.selectedIdentity,
            type: PPD_CART_ITEM_TYPES.PRINT_PACKAGE,
          },
        ],
      };
    }

    case DESELECT_PRINT_PACKAGE:
      return {
        ...state,
        cart: state.cart.filter(cartItem => {
          return !isExistingItem(
            { ...newItem(), type: PPD_CART_ITEM_TYPES.PRINT_PACKAGE },
            cartItem,
            'printPackage',
          );
        }),
      };

    case SELECT_SUBSCRIPTION:
      return {
        ...state,
        cart: [
          ...state.cart,
          {
            id: uuidv4(),
            type: PPD_CART_ITEM_TYPES.SUBSCRIPTION,
            selectedIdentity: action.selectedIdentity,
          },
        ],
      };

    case DESELECT_SUBSCRIPTION:
      return {
        ...state,
        cart: state.cart.filter(cartItem => {
          return (
            cartItem.selectedIdentity.id !== action.selectedIdentity.id ||
            cartItem.type !== PPD_CART_ITEM_TYPES.SUBSCRIPTION
          );
        }),
      };

    case SELECT_ALBUM_CUSTOM_PRINT: {
      const item = {
        id: uuidv4(),
        type: PPD_CART_ITEM_TYPES.ALBUM_CUSTOM_PRINT,
        selectedIdentity: action.selectedIdentity,
        albumCustomPrint: action.albumCustomPrint,
        includeShipping: action.includeShipping,
        quantity: action.quantity || 1,
      };

      const isInCart = state.cart.find(cartItem =>
        isExistingItem(item, cartItem),
      );
      const cart = isInCart
        ? state.cart.map(cartItem => {
            if (isExistingItem(item, cartItem)) {
              return item;
            }

            return cartItem;
          })
        : [...state.cart, item];

      return {
        ...state,
        cart,
      };
    }

    case DESELECT_ALBUM_CUSTOM_PRINT:
      return {
        ...state,
        cart: state.cart.filter(
          item =>
            item.type !== PPD_CART_ITEM_TYPES.ALBUM_CUSTOM_PRINT ||
            item.selectedIdentity.id !== action.selectedIdentity.id ||
            item.albumCustomPrint.id !== action.albumCustomPrint.id,
        ),
      };

    case DESELECT_CART_ITEM:
      return {
        ...state,
        cart: state.cart.filter(item => item.id !== action.id),
      };

    case CHECKOUT_REQUEST:
      return { ...state, loading: true, error: '', success: false };

    case CHECKOUT_SUCCESS:
      return {
        ...state,
        cart: [],
        purchasedItems: state.cart,
        loading: false,
        error: '',
        success: true,
      };

    case CHECKOUT_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.error.message || action.error,
        success: false,
      };

    case SELECT_IDENTITY:
      return { ...state, selectedIdentity: action.selectedIdentity };

    default:
      return state;
  }
};

export { initialState };
export default ppdPurchaseReducer;
